'use client';
import { Dialog, Portal } from '@ark-ui/react';
import { usePathname } from 'next/navigation';
import { useCallback } from 'react';
import { memo } from 'react';
import { MdClose } from 'react-icons/md';
import { ButtonNextLink } from '~/app/_components/button';
import { useClientOnlyMediaQuery } from '~/common/hooks/use-client-only-media-query';
import { routes } from '~/utils/routes';
import { Color } from '~/utils/styles/enums';
import { Media } from '~/utils/styles/enums';
import { Link } from '@app/_components/link';
import { css } from '@styled-system/css';
import { Paragraph, VStack, HStack } from '@styled-system/jsx';
import { styled } from '@styled-system/jsx';
export type ModalProps = {
  isOpen: boolean;
  onClose?: () => void;
  userId: string;
};
type Props = {} & ModalProps;

const _LoginRequiredModal = ({ isOpen, onClose, userId }: Props) => {
  const screenLarge = useClientOnlyMediaQuery(Media.LARGE);
  const isMobile =
    typeof screenLarge === 'undefined' ? undefined : !screenLarge;

  const currentPath = usePathname();

  const handleOnOpenChange = useCallback(
    ({ open }: { open: boolean }) => {
      if (!open) {
        onClose?.();
      }
    },
    [onClose]
  );
  return (
    <Dialog.Root
      lazyMount
      unmountOnExit
      open={isOpen}
      onOpenChange={handleOnOpenChange}
    >
      <Portal>
        <Backdrop />
        <Positioner>
          <Content>
            <Dialog.CloseTrigger
              className={css({
                cursor: 'pointer',
                position: 'absolute',
                right: '0',
                top: '-36px',
              })}
            >
              <MdClose color={Color.WHITE} size={24} aria-label="閉じる" />
            </Dialog.CloseTrigger>
            {/* Screen large content */}
            {!isMobile && (
              <>
                <VStack gap="16px" mb="16px" w="100%">
                  <Paragraph
                    lineHeight="24px"
                    fontSize="size_18"
                    fontWeight="700"
                  >
                    ログインしてください
                  </Paragraph>
                  <Paragraph fontSize="size_14" color="accent.500">
                    ログインすると、あなたの好みを学習して最適な作品を届けるAI機能、閲覧履歴、いいね、コメントの投稿などができるようになります。
                  </Paragraph>
                </VStack>
                <VStack gap="4" px="4">
                  <ButtonNextLink
                    width="104px"
                    type="pill"
                    marginBottom="16px"
                    href={routes.login.url(currentPath)}
                  >
                    ログイン
                  </ButtonNextLink>
                  <AuthLink color="tint" href={routes.register.url()}>
                    無料会員登録がこちら
                  </AuthLink>
                </VStack>
              </>
            )}

            {isMobile && (
              <>
                <VStack gap="8px" mb="16px" w="100%">
                  <Paragraph
                    lineHeight="24px"
                    fontSize="size_18"
                    fontWeight="700"
                  >
                    お気に入りの作家をフォローして <br />
                    最新話をチェックしよう
                  </Paragraph>
                  <Paragraph fontSize="size_12" color="accent.500">
                    フォローするにはログインが必要です。
                  </Paragraph>
                </VStack>
                <VStack gap="4" px="4" width="100%">
                  <ButtonNextLink
                    type="pill"
                    href={`https://tellernovel.onelink.me/sLTh?pid=web&af_dp=teller%3A%2F%2&deep_link_value=${encodeURIComponent(
                      `teller://user/${userId}`
                    )}`}
                  >
                    アプリでひらく
                  </ButtonNextLink>
                  <HStack
                    alignContent="center"
                    justifyContent="center"
                    gap="8px"
                    marginTop="16px"
                  >
                    <AuthLink href={routes.register.url()}>
                      無料会員登録
                    </AuthLink>
                    <span>/</span>
                    <AuthLink href={routes.login.url(currentPath)}>
                      ログイン
                    </AuthLink>
                  </HStack>
                </VStack>
              </>
            )}
          </Content>
        </Positioner>
      </Portal>
    </Dialog.Root>
  );
};

const AuthLink = styled(Link, {
  base: {
    color: 'tint',
  },
});

const Backdrop = styled(Dialog.Backdrop, {
  base: {
    backdropFilter: 'blur(20px)',
    background: 'rgba(0, 0, 0, 0.5)',
    height: '100vh',
    left: '0',
    position: 'fixed',
    top: '0',
    width: '100vw',
    zIndex: 'teller_modal',
    _open: {
      animation: 'fadein 150ms ease-out',
    },
    _closed: {
      animation: 'fadeout 150ms ease-out',
    },
  },
});

const Positioner = styled(Dialog.Positioner, {
  base: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'center',
    position: 'fixed',
    top: {
      base: 0,
      lg: 24,
    },
    bottom: {
      base: 0,
      lg: 24,
    },
    left: {
      base: 0,
      lg: undefined,
    },
    right: {
      base: 0,
      lg: undefined,
    },
    margin: {
      base: 0,
      lg: 'auto',
    },
    height: {
      base: '100dvh',
      lg: '600px',
    },
    width: {
      base: '100dvw',
      lg: '400px',
    },
    zIndex: 'teller_modal',
  },
});

const Content = styled(Dialog.Content, {
  base: {
    background: 'white',
    boxShadow: 'lg',
    textAlign: 'center',
    borderRadius: {
      base: '8px',
    },
    padding: {
      lg: '40px',
      sm: '40px 20px',
    },
    py: '40px',
    width: '90%',
    position: 'relative',
    _open: {
      animation: 'fadein 150ms ease-out',
    },
    _closed: {
      animation: 'fadeout 150ms ease-out',
    },
  },
});

export const LoginRequiredModal = memo(_LoginRequiredModal);
